import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@design-system/components/modal/modal';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';

type Mode = 'add' | 'edit';

type Props = {
  open: boolean;
  mode: Mode;
  initialLabel: string;
  initialHref: string;
  onClose: () => void;
  onSave: (payload: { name: string; href: string }) => void;
};

export const MDLinkModal = ({
  open,
  mode,
  initialLabel,
  initialHref,
  onClose,
  onSave,
}: Props) => {
  const [name, setName] = useState(initialLabel);
  const [href, setHref] = useState(initialHref);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const hrefRef = useRef<HTMLInputElement | null>(null);

  const isValidHref = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    const allowedSchemes = ['http:', 'https:', 'mailto:', 'tel:'];
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed);

    if (hasScheme && !allowedSchemes.some((s) => trimmed.startsWith(s))) {
      return false;
    }

    if (trimmed.startsWith('mailto:')) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.slice(7));
    }

    if (trimmed.startsWith('tel:')) {
      return /^tel:\+?[0-9()\\-\\s]+$/.test(trimmed);
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      try {
        // eslint-disable-next-line no-new
        new URL(trimmed);
        return true;
      } catch {
        return false;
      }
    }

    // Disallow when no scheme to prevent invalid/unsafe URLs
    return false;
  }, []);

  const hrefValid = isValidHref(href);
  useEffect(() => {
    if (open) {
      setName(initialLabel);
      setHref(initialHref);
      requestAnimationFrame(() => {
        const input = nameRef.current;
        if (input) {
          const len = input.value.length;
          input.focus();
          input.setSelectionRange(len, len);
        }
      });
    }
  }, [initialHref, initialLabel, open]);

  const isValid = name.trim().length > 0 && hrefValid;
  const primaryLabel = mode === 'add' ? 'Insert' : 'Save';
  const titleLabel = mode === 'add' ? 'Add Link' : 'Edit Link';

  const handleSave = useCallback(() => {
    if (!isValid) return;
    onSave({ name: name.trim(), href: href.trim() });
  }, [href, isValid, name, onSave]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={titleLabel}
      primaryCta={{
        caption: primaryLabel,
        onClick: handleSave,
        state: isValid ? undefined : 'disabled',
        className: sprinkles({
          borderRadius: 'full',
          px: 'l',
          py: 'm',
        }),
      }}
      secondaryCta={{
        caption: 'Cancel',
        onClick: onClose,
        className: sprinkles({ px: 'm' }),
      }}
    >
      <Col gap="l" className={sprinkles({ width: '100%' })}>
        <label
          className={sprinkles({
            display: 'flex',
            flexDirection: 'column',
            gap: 's',
          })}
        >
          <Typography variant="body" size="medium" fontWeight="medium">
            Text
          </Typography>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            ref={nameRef}
            className={sprinkles({
              width: '100%',
              borderRadius: 'm',
              p: 'm',
            })}
            style={{
              border: '1px solid #e0e0e0',
              background: '#F8F8F8',
              fontFamily: 'Visuelt, sans-serif',
              fontSize: 16,
              fontWeight: 400,
              lineHeight: '24px',
              outline: 'none',
            }}
          />
        </label>

        <label
          className={sprinkles({
            display: 'flex',
            flexDirection: 'column',
            gap: 's',
          })}
        >
          <Typography variant="body" size="medium" fontWeight="medium">
            Link
          </Typography>
          <input
            value={href}
            onChange={(e) => setHref(e.target.value)}
            ref={hrefRef}
            className={sprinkles({
              width: '100%',
              borderRadius: 'm',
              p: 'm',
            })}
            style={{
              border: '1px solid #e0e0e0',
              background: '#F8F8F8',
              fontFamily: 'Visuelt, sans-serif',
              fontSize: 16,
              fontWeight: 400,
              lineHeight: '24px',
              outline: 'none',
            }}
          />
          {!hrefValid && href.trim() !== '' && (
            <Typography
              variant="body"
              size="small"
              fontWeight="medium"
              color="ERROR-100"
            >
              Enter a valid URL (http, https, mailto, tel).
            </Typography>
          )}
        </label>
      </Col>
    </Modal>
  );
};
